// src/pages/legal/TermsAndConditionsPage.js
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import EvStationIcon from '@mui/icons-material/EvStation';

const TermsAndConditionsPage = () => {

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 2,
            background: (theme) => theme.palette.background.paper,
            boxShadow: (theme) => `0 4px 24px 0 ${theme.palette.primary.light}22`
          }}
        >
          {/* Header */}
          <Box display="flex" alignItems="center" mb={4}>
            <EvStationIcon 
              sx={{ 
                fontSize: 32, 
                color: 'primary.main',
                mr: 2
              }} 
            />
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
              Terms & Conditions
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Effective Date: 25-Jul-2025
          </Typography>

          <Typography variant="body1" paragraph>
            Welcome to GetWatted's Charging Station Management System (CSMS). By creating an account and accessing our platform and services, you agree to comply with and be legally bound by the following Terms and Conditions.
          </Typography>

          <Typography variant="body1" paragraph>
            Please read them carefully. If you do not agree to these terms, you may not use our services.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By checking the "I agree" box and creating an account, you acknowledge that:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· You have read, understood, and accepted these Terms and Conditions." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· You are legally authorized to enter into binding agreements." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· You consent to being charged automatically for EV charging services via the payment method you provide." />
              </ListItem>
            </List>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              2. Account Registration
            </Typography>
            <Typography variant="body1" paragraph>
              To use our software and charging services, you must:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· Create an account with a valid email and password." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Provide accurate, current, and complete information during registration." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Maintain the security and confidentiality of your login credentials." />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              You are responsible for all activity under your account.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              3. Use of Charging Services
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to use the EV chargers in accordance with:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· All applicable laws and regulations." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Instructions and limits as displayed on the charging station or mobile/web app." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Safe, responsible practices while operating a motor vehicle." />
              </ListItem>
            </List>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              4. Automatic Payment Authorization
            </Typography>
            <Typography variant="body1" paragraph>
              You understand and agree that:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· By using our EV charging services, you authorize us to charge your linked debit or credit card via Stripe for the full cost of energy delivered to your vehicle." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Charging sessions are calculated based on energy consumed (kWh), time used, or session fee depending on the station." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Charges may include taxes, fees, idle time fees, or overstay penalties if applicable." />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              You may update your payment method at any time in your account settings.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              5. Stripe Payment Processing
            </Typography>
            <Typography variant="body1" paragraph>
              We use Stripe, a third-party payment processor, to securely handle all payments. By agreeing to these Terms, you also agree to Stripe's Terms of Service.
            </Typography>
            <Typography variant="body1" paragraph>
              We do not store full credit/debit card numbers on our servers.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              6. User Responsibilities
            </Typography>
            <Typography variant="body1" paragraph>
              You agree not to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· Attempt to access chargers or software in any unauthorized manner." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Interfere with or disrupt the operation of the platform or EV chargers." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Use the system for any unlawful, fraudulent, or harmful purposes." />
              </ListItem>
            </List>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              7. Termination and Suspension
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to suspend or terminate your access to the platform and charging services if:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· You violate these Terms." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· There are issues with your payment method." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· We suspect fraudulent or unauthorized activity." />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              You may close your account at any time via the app or by contacting support.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              8. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              We are not liable for:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="· Any direct or indirect damages caused by misuse, unavailability, or performance issues of the chargers or software." />
              </ListItem>
              <ListItem>
                <ListItemText primary="· Charging delays or failures due to power outages, station errors, or third-party services." />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              Use of our services is at your own risk.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              9. Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Your data is handled according to our Privacy Policy, which describes how your personal and payment information is collected, used, and stored.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              10. Modifications
            </Typography>
            <Typography variant="body1" paragraph>
              We may update these Terms from time to time. Changes will be effective upon posting. Continued use of the platform after changes constitutes your acceptance.
            </Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="h6" gutterBottom color="primary.main">
              11. Contact
            </Typography>
            <Typography variant="body1" paragraph>
              For questions or support, contact us at: Email: customerservice@getwatted.nz Phone: 0 800 …………
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary" mb={2}>
              By checking the box, you confirm that you have read, understood, and agreed to these Terms and Conditions, and you authorize automatic billing for EV charging services.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              © 2025 GetWatted. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsAndConditionsPage;